import { createClient } from "@supabase/supabase-js";
import type { GameType, Category } from "./types";
import { cookies } from "next/headers";

const supabase = createClient(process.env.NEXT_PUBLIC_SUPABASE_URL!, process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!);

// 게임 리스트 가져오기 (limit 적용)
export async function fetchGames(limit: number = 0): Promise<GameType[]> {
  let query = supabase.from("games").select("*");

  if (limit > 0) {
    query = query.limit(limit);
  }

  const { data, error } = await query;
  if (error) {
    console.error("Error fetching games:", error);
    return [];
  }

  return data || [];
}

// 게임 리스트 가져오기
export async function fetchGamesByRange(category: string | null, from: number | null, to: number): Promise<GameType[]> {
  let query = supabase.from("games").select("*");

  if (category !== null) {
    query = query.contains("categories", [category]);
  }

  if (from === null) {
    query = query.limit(to);
  } else {
    query = query.range(from, to);
  }

  const { data, error } = await query;
  if (error) {
    console.error("Error fetching games:", error);
    return [];
  }

  return data || [];
}

// 특정 게임 상세 정보 가져오기
export async function fetchGameDetails(encodedSlug: string): Promise<GameType | null> {
  let slug: string;
  try {
    slug = decodeURIComponent(encodedSlug);
  } catch (e) {
    console.error("슬러그 디코딩 오류:", e);
    slug = encodedSlug; // 디코딩 실패 시 원본 사용
  }
  
  console.log(`Supabase 게임 조회 시도: encodedSlug=${encodedSlug}, decodedSlug=${slug}`);
  
  try {
    // 먼저 정확한 일치로 검색
    let { data, error } = await supabase
      .from("games")
      .select("*")
      .eq("slug", slug)
      .single();

    if (error || !data) {
      console.log(`정확한 일치로 게임을 찾을 수 없음, 부분 일치로 시도: slug=${slug}`);
      
      // 부분 일치로 검색
      const { data: partialMatches, error: partialError } = await supabase
        .from("games")
        .select("*")
        .ilike("slug", `%${slug}%`)
        .limit(1);

      if (partialError) {
        console.error("Supabase 게임 조회 오류:", partialError);
        return null;
      }

      if (!partialMatches || partialMatches.length === 0) {
        console.log(`Supabase에서 게임을 찾을 수 없음: slug=${slug}`);
        return null;
      }

      data = partialMatches[0];
    }

    console.log(`Supabase에서 게임 조회 성공:`, data);
    return data;
  } catch (error) {
    console.error("Supabase 게임 조회 중 예외 발생:", error);
    return null;
  }
}

// 모든 게임 가져오기 (limit 적용)
export async function getAllGames(limit: number = 12): Promise<GameType[]> {
  return await fetchGames(limit);
}

// 특정 게임 데이터 가져오기
export async function getGameData(slug: string): Promise<GameType | null> {
  return await fetchGameDetails(slug);
}

// 추천 게임 가져오기
export async function getFeaturedGame(): Promise<GameType | null> {
  const { data, error } = await supabase.from("games").select("*").eq("is_featured", true).limit(1).single();

  if (error) {
    console.error("Error fetching featured game:", error);
    return null;
  }

  return data;
}

// 관련 게임 가져오기
export async function getRelatedGames(gameId: string, limit: number = 12): Promise<GameType[]> {
  const supabase = createClient(process.env.NEXT_PUBLIC_SUPABASE_URL!, process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!);
  
  try {
    // 1. 현재 게임의 카테고리 가져오기
    const { data: currentGame, error: gameError } = await supabase
      .from('games')
      .select('categories')
      .eq('id', gameId)
      .single();

    if (gameError) throw gameError;
    if (!currentGame?.categories) return [];

    // 카테고리가 문자열인 경우 배열로 변환
    const categories = Array.isArray(currentGame.categories) 
      ? currentGame.categories 
      : typeof currentGame.categories === 'string'
        ? currentGame.categories.split(',').map(c => c.trim())
        : [];

    // 2. 각 카테고리별로 관련 게임 조회
    const relatedGamesPromises = categories.map(category =>
      supabase
        .from('games')
        .select('id, title, slug, thumbnail, categories, views, plays, likes, isNew, description, gameUrl, dateAdded, developer')
        .contains('categories', [category])
        .neq('id', gameId) // 현재 게임 제외
        .order('views', { ascending: false })
        .limit(limit)
    );

    const relatedGamesResults = await Promise.all(relatedGamesPromises);
    
    // 3. 결과를 하나의 배열로 합치고 중복 제거
    const allRelatedGames = relatedGamesResults
      .flatMap(result => result.data || [])
      .filter((game, index, self) => 
        index === self.findIndex(g => g.id === game.id)
      );

    // 4. 조회수 순으로 정렬하고 limit 적용
    return allRelatedGames
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .slice(0, limit);

  } catch (error) {
    console.error('Error fetching related games:', error);
    return [];
  }
}

// 카테고리 목록 가져오기
export async function getCategories(): Promise<Category[]> {
  const { data, error } = await supabase.from("games").select("categories");

  if (error) {
    console.error("Error fetching categories:", error);
    return [];
  }

  const categoriesSet = new Set<string>();

  data.forEach((game) => {
    game.categories.forEach((category: string) => categoriesSet.add(category));
  });

  return Array.from(categoriesSet).map((name, index) => ({
    id: index + 1,
    name,
  }));
}

// 신규 게임 가져오기 (최근 추가된 게임)
export async function getNewGames(limit: number = 12): Promise<GameType[]> {
  const { data, error } = await supabase
    .from("games")
    .select("*")
    .order("dateAdded", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Error fetching new games:", error);
    return [];
  }

  return data || [];
}

// 인기 게임 가져오기 (조회수 기준)
export async function getPopularGames(limit: number = 12): Promise<GameType[]> {
  const { data, error } = await supabase
    .from("games")
    .select("*")
    .order("plays", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Error fetching popular games:", error);
    return [];
  }

  return data || [];
}

// 랜덤 게임 가져오기 (랜덤 오프셋 방식)
export async function getRandomGames(limit: number = 12): Promise<GameType[]> {
  try {
    // 1. 전체 게임 개수 가져오기
    const { count, error: countError } = await supabase
      .from('games')
      .select('*', { count: 'exact', head: true }); // head: true는 데이터 없이 개수만 가져옴

    if (countError) {
      console.error("Error fetching game count:", countError);
      throw countError;
    }

    if (count === null || count === 0) {
      console.warn("No games found to select random games from.");
      return [];
    }

    // 요청할 게임 수(limit)가 전체 개수보다 많으면 모든 게임 반환
    if (limit >= count) {
      return await fetchGames(); // 모든 게임 가져오기
    }

    // 2. 랜덤 오프셋 계산 (0 ~ count - limit)
    const maxOffset = count - limit;
    const randomOffset = Math.floor(Math.random() * (maxOffset + 1));

    // 3. 계산된 오프셋부터 limit 개수만큼 게임 조회
    const { data, error: fetchError } = await supabase
      .from('games')
      .select('*')
      .range(randomOffset, randomOffset + limit - 1); // range는 inclusive

    if (fetchError) {
      console.error("Error fetching random games with offset:", fetchError);
      throw fetchError;
    }

    return data || [];

  } catch (error) {
    // 최종 오류 처리
    console.error("Failed to get random games:", error);
    return []; // 오류 발생 시 빈 배열 반환
  }
}
